import intersectionWith from "lodash/intersectionWith";
import isMatch from "lodash/isMatch";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { useMediaQueries } from "Shared/Responsive";
import styled, { ThemeContext } from "styled-components";
import PlusIcon from "src/components/icons/Plus";
import { ApplicationStore } from "src/store";
import { toggleGarageDrawer } from "src/store/actions/garageDrawer";

const Cell = styled("div")`
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 0.5em 0 1em;
    align-items: center;

    ${(props) => props.theme.breakpoints.up("tablet")} {
        padding: 0 1em;
    }
`;

const Button = styled("button")`
    background: none;
    border: none;
    color: ${(props) => props.theme.palette.primary.main};
    cursor: pointer;
    font-size: 1em;
    outline: 0;
    padding: 0;
`;

const ButtonIcon = styled(PlusIcon)`
    color: ${(props) => props.theme.palette.primary.main};
    height: 15px;
    width: 15px;
`;

interface Props {
    vin: string;
}

const ConditionalRebatesCell = ({
    selected,
    vin,
    isOpen,
    toggleDrawer,
}: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
    const location = useLocation<{ vin?: string }>();
    const themeContext = useContext(ThemeContext);
    const { t } = useTranslation();
    const { mobile } = useMediaQueries();

    const handleToggle = () => {
        if (!isOpen || (location.pathname === `/my-garage/conditional-rebates` && location.state?.vin === vin)) {
            toggleDrawer();
        }
    };

    return (
        <Link
            to={{ pathname: `/my-garage/conditional-rebates`, state: { vin } }}
            onClick={() => handleToggle()}
            style={{ color: "inherit", height: "100%", width: "100%", display: "flex", alignItems: "center" }}
            title={t("MyGarage.Cells.titles.conditionalRebates")}
        >
            <Cell>
                {selected ? (
                    <span>{t(`MyGarage.TableCompare.conditionalRebatesAddedCellText`)}</span>
                ) : (
                    <span style={{ color: themeContext.palette.primary.main }}>
                        {mobile
                            ? t(`MyGarage.TableCompare.conditionalRebatesDefaultCellTextMobile`)
                            : t(`MyGarage.TableCompare.conditionalRebatesDefaultCellText`)}
                    </span>
                )}
                <Button className="clickable">
                    <ButtonIcon />
                </Button>
            </Cell>
        </Link>
    );
};

const mapStateToProps = (state: ApplicationStore, { vin }: Props) => {
    const mode = state.account.integration_settings?.Financing.conditional_rebates as "data-driven" | "hand-raisers";
    return {
        selected:
            mode === "data-driven"
                ? intersectionWith(
                      state.conditionalRebates.dataDriven.available[vin],
                      state.conditionalRebates.dataDriven.selected,
                      isMatch
                  ).length > 0
                : Object.values(state.conditionalRebates.handRaisers).some((value) => value),
        isOpen: state.onlineShopper.isDrawerOpen,
    };
};

const mapDispatchToProps = { toggleDrawer: toggleGarageDrawer };

export default connect(mapStateToProps, mapDispatchToProps)(ConditionalRebatesCell);
